import { Button, Flex, useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import {
  ContractTaskDto,
  UpdateTaskRequest,
  PersonDto,
  useListParticipantsForContractsQuery,
  } from "../../../../../../autogen/bff-api";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { useSaveNow } from "../../../../../../common/editing/save-coordinator";
import { contractTaskEditorId } from "../useContractTaskEditor";
import { PersonSelector } from "../../../../../../common/persons/PersonSelector";
import { useTaskDelegationConfirmation } from "../../useTaskDelegationConfirmation";

interface Props {
  task: ContractTaskDto;
  onEditComleted: () => void;
  onCancel: () => void;
}

export const EditResponsiblePerson = ({ task, onEditComleted, onCancel }: Props) => {
  const { saveNow } = useSaveNow<ContractTaskDto, UpdateTaskRequest>(contractTaskEditorId(task.id));
  const [isLoadingResponsiblePerson, setIsLoadingResponsiblePerson] = useState(false);
  const toast = useToast();
  const apiError = useApiError();
  const [person, setPerson] = useState<PersonDto>(task.responsiblePerson);
  const { delegateConfirmingLoss, modal: delegationConfirmation } = useTaskDelegationConfirmation(task.contractId);

  const { data: participants, isLoading: isLoadingParticipants } = useListParticipantsForContractsQuery({
    contractId: task.contractId,
  });

  if (isLoadingParticipants) return <div>{t("Loading")}...</div>;

  if (!participants) return <div>{t("Something is wrong")}...</div>;

  const save = async () => {
    setIsLoadingResponsiblePerson(true);
    try {
      await saveNow({
        responsiblePerson: person.id,
      });
      setIsLoadingResponsiblePerson(false);
      onEditComleted();
    } catch (e) {
      setIsLoadingResponsiblePerson(false);
      toast({
        title: t("Could not edit responsible person"),
        description: apiError.message(e),
        status: "error",
      });
    }
  };

  const saveConfirmingLoss = async () => {
    setIsLoadingResponsiblePerson(true);
    await delegateConfirmingLoss({
      responsible: person,
      taskId: task.id,
      delegate: save,
      onCancelled: () => {
        setPerson(task.responsiblePerson);
        setIsLoadingResponsiblePerson(false);
      },
    });
  };

  return (
    <Flex flexDirection={"column"} width="full" mt="1">
      <PersonSelector
        organizationIds={[
          ...[participants.owningOrganization.id],
          ...(participants?.organizationParticipants.map((p) => p.id) ?? []),
        ]}
        selectedPerson={person}
        onChange={(value) => {
          if (value) setPerson(value);
        }}
        additionalPersons={participants.personParticipants}
        placeholder={t("Select responsible person") ?? ""}
      />
      <Flex pt="2.5">
        <Button
          id="save-responsible-person-button"
          variant="solid"
          colorScheme="teal"
          size="xs"
          isLoading={isLoadingResponsiblePerson}
          onClick={() => void saveConfirmingLoss()}
        >
          {t("Save")}
        </Button>
        <Button variant={"solid"} colorScheme="gray" size="xs" ml="5px" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </Flex>
      {delegationConfirmation}
    </Flex>
  );
};
